import { bindActionCreators } from '@reduxjs/toolkit';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { mapVehicleData, track, trackConversionEvent } from '../../actions/tracking';
import { LinkButton } from '../../components/button';
import { getItem, getItemId } from '../../selectors/item';
import { getItemRetailerContactOptions } from '../../selectors/item-retailer';
import ClickToViewState from '../../shared/click-to-view-states';
import { conversionEvents, eventTypes } from '../../shared/tracking';

let stored: null | {
  state: { currentState: typeof ClickToViewState.Hidden };
  id: string | null;
} = null;
const getIntialState = (id: string | null) => {
  if (!stored || id !== stored.id) {
    return { currentState: ClickToViewState.Hidden };
  }
  return stored.state;
};

interface IEmailButton {
  isLight?: boolean;
  isMedium?: boolean;
  label: string;
}

const EmailButton = ({ isLight, isMedium, label }: IEmailButton) => {
  const { t } = useTranslation(['global', 'item']);
  const dispatch = useDispatch();
  const item = useSelector(getItem);
  const id = useSelector(getItemId);
  const contactOptions = useSelector(getItemRetailerContactOptions);
  const actions = bindActionCreators(
    {
      trackConversionEvent,
      track,
    },
    dispatch,
  );

  const [state, setState] = useState(getIntialState(id));

  useEffect(() => {
    if (state.currentState === ClickToViewState.Visible) {
      stored = { state: { ...state }, id };
    }
  }, [state.currentState]);

  if (!item || !contactOptions || !contactOptions.email) return null;

  const { email } = contactOptions;

  const onClick = (e: React.MouseEvent) => {
    if (state.currentState === ClickToViewState.Hidden) {
      e.stopPropagation();
      e.preventDefault();
      actions.trackConversionEvent(item, conversionEvents.CONVERSION_MAIL_VISIBLE);
    }

    if (state.currentState === ClickToViewState.Visible) {
      actions.track(eventTypes.OTHER, {
        item: mapVehicleData(item, true),
        conversionType: conversionEvents.CONVERSION_MAIL_SENT,
      });
    }

    setState({ currentState: ClickToViewState.Visible });
  };

  const { currentState } = state;

  if (currentState === ClickToViewState.Visible) {
    const subject = encodeURIComponent(
      t('sendEmailSubject', {
        ns: 'item',
        brandName: t('brandName', { ns: 'global' }),
        item: `${item.registrationNumber}, ${item.manufacturer} ${item.modelSeries}`,
      }),
    );

    return (
      <LinkButton
        href={`mailto:${email}?subject=${subject}`}
        title={t('sendEmailTo', { ns: 'item', email })}
        modifiers={`${isLight ? 'action-light' : 'action'} full-width ${isMedium ? 'medium' : null}`}
        onClick={onClick}
      >
        <i className="icon-email m-r-half" />
        {email}
      </LinkButton>
    );
  }

  return (
    <button
      type="button"
      data-wx-button={`${isLight ? 'action-light' : 'action'} full-width ${isMedium ? 'medium' : null}`}
      title={label}
      onClick={onClick}
    >
      <i className="icon-email m-r-half" />
      {label}
    </button>
  );
};

export default EmailButton;
